import mapboxgl from "mapbox-gl";
import { Injectable } from "@angular/core";
import { ReplaySubject, Subject } from "rxjs";
import { MatDialog } from "@angular/material/dialog";
import { allLayers } from "../constants/layer";
import { CommonService } from "./common.service";
import { UtilitiesFn } from "../constants/utilities";
import { baseLayerStyles, iCons, ToolTypes } from "../constants/map";
import { MapboxglGraphControl } from "../components/controls/mapboxgl-graph/mapboxgl-graph-control";
import { MapboxglLayerControl } from "../components/controls/mapboxgl-layer/mapboxgl-layer-control";
import { MapboxglSearchControl } from "../components/controls/mapboxgl-search/mapboxgl-search-control";
import { MapboxglChangeStyleControl } from "../components/controls/mapboxgl-change-style/mapboxgl-change-style-control";
import { MapboxglDrawFeatureControl } from "../components/controls/mapboxgl-draw-feature/mapboxgl-draw-feature-control";
import { MapboxglFeatureInfoControl } from "../components/controls/mapboxgl-feature-info/mapboxgl-feature-info-control";
import { MapboxglCustomZoomInControl } from "../components/controls/mapboxgl-custom-navigation-control/mapboxgl-custom-zoom-in-control";
import { MapboxglCustomZoomOutControl } from "../components/controls/mapboxgl-custom-navigation-control/mapboxgl-custom-zoom-out-control";
import { MapboxglCustomCompassControl } from "../components/controls/mapboxgl-custom-navigation-control/mapboxgl-custom-compass-control";

@Injectable({
  providedIn: "root",
})
export class MapboxGlService {
  // Map configs
  public map: any;
  public layers: any[];
  public mapConfigs = {
    zoom: 10,
    element: "map",
    center: [108.863, 11.6739],
  };

  // Controls
  // public navControl: any;
  public graphControl: MapboxglGraphControl;
  public layerControl: MapboxglLayerControl;
  public searchControl: MapboxglSearchControl;
  public customDrawControl: MapboxglDrawFeatureControl;
  public changeStyleControl: MapboxglChangeStyleControl;
  public featureInfoControl: MapboxglFeatureInfoControl;
  private customZoomInControl: MapboxglCustomZoomInControl;
  private customZoomOutControl: MapboxglCustomZoomOutControl;
  private customCompassControl: MapboxglCustomCompassControl;

  // Subjects
  public mapOnLoadedSubject: Subject<any> = new ReplaySubject(1);
  public mapOnClickedSubject: Subject<any> = new ReplaySubject(1);
  public drawFeatureSubject: Subject<any> = new ReplaySubject(1);
  public layersOnLoadedSubject: Subject<any> = new ReplaySubject(1);
  public controlOnLoadedSubject: Subject<any> = new ReplaySubject(
    ToolTypes.simple_select as any
  );

  constructor(
    public dialog: MatDialog,
    public utilityFn: UtilitiesFn,
    public commonService: CommonService
  ) {
    this.showMap = this.showMap.bind(this);
    this.changeMapStyle = this.changeMapStyle.bind(this);
    this.mapOnDraw = this.mapOnDraw.bind(this);
    this.mapOnLoaded = this.mapOnLoaded.bind(this);
    this.getMapFeature = this.getMapFeature.bind(this);
  }

  /**
   * Thay đổi lớp bản đồ nền
   * @param style baseLayerStyles
   */
  changeMapStyle = async (style) => {
    // Change style link
    for (const item of baseLayerStyles) {
      if (item.id === style.id) {
        item.base = true;
        this.map.setStyle(null);
        this.map.setStyle(item.link);
      } else {
        item.base = false;
      }
    }
  };

  /**
   * Khởi tạo bản đồ
   */
  showMap = (
    element: string = "mapViewDiv",
    zoom: number = 10,
    center: number[] = [108.863, 11.6739]
  ) => {
    // Save map config
    this.mapConfigs.zoom = zoom;
    this.mapConfigs.center = center;
    this.mapConfigs.element = element;

    // Style, Access token
    const style: any = baseLayerStyles.find(({ base }) => base);
    (mapboxgl as typeof mapboxgl).accessToken =
      "pk.eyJ1IjoiYW5uZ3V5ZW5zZHZuIiwiYSI6ImNtMGdmOGFsajAwNXoyaXFqY3BlOTkyNHoifQ.aP-GpIi-2geed_6O8rJ8mg";
    this.map = new mapboxgl.Map({
      container: element,
      style: style.link,
      zoom,
      center: center as any,
      maxTileCacheSize: 1000000,
    })
      .on("load", () => {
        this.mapOnLoaded();
      })
      .on("style.load", () => {
        this.mapOnStyleLoad();
        addIcons().then();
      })
      .on("click", (e) => this.mapOnClickedSubject.next(e))
      .on("draw.create", this.mapOnDraw)
      .on("draw.delete", this.mapOnDraw)
      .on("draw.update", this.mapOnDraw);

    const addIcons = async () => {
      // Add images
      iCons.forEach((icon) => {
        this.map.loadImage(icon.image, (error: any, image: any) => {
          this.map.addImage(icon.type, image, { sdf: false });
        });
      });
    };
  };

  /**
   * Sau khi thêm/sửa/xóa feature
   * @param e Value callback
   */
  mapOnDraw = (e) => {
    this.drawFeatureSubject.next(e);
  };

  mapOnLoaded = () => {
    // Add default control
    // this.addNavControl();

    // Add custom control
    this.addCustomZoomInControl().then();
    this.addCustomZoomOutControl().then();
    this.addCustomCompassControl().then();

    // Loaded subject
    this.mapOnLoadedSubject.next(null);
  };

  mapOnStyleLoad = () => {
    if (this.layers) {
      this.addLayers(this.layers);
      // for (const layer of this.layers) {
      //     if (layer.id === 'Dibrugarh_boundary') {
      //         if (this.map.getStyle().name !== 'Mapbox Satellite') {
      //             layer.polylineStyle.paint['line-color'] = 'black';
      //         } else {
      //             layer.polylineStyle.paint['line-color'] = '#ebeaea';
      //         }
      //     }
      // }
    }
  };

  /**
   * Thêm mới nút zoom in
   * @param position Vị trí hiển thị
   */
  addCustomZoomInControl = async (position: string = "top-right") => {
    this.customZoomInControl = new MapboxglCustomZoomInControl({
      mapboxglService: this,
    });
    this.map.addControl(this.customZoomInControl, position);
  };

  /**
   * Thêm mới nút zoom out
   * @param position Vị trí hiển thị
   */
  addCustomZoomOutControl = async (position: string = "top-right") => {
    this.customZoomOutControl = new MapboxglCustomZoomOutControl({
      mapboxglService: this,
    });
    this.map.addControl(this.customZoomOutControl, position);
  };

  /**
   * Thêm mới nút la bàn dể quay về vị trí mặc định
   * @param position Vị trí hiển thị
   */
  addCustomCompassControl = async (position: string = "top-right") => {
    this.customCompassControl = new MapboxglCustomCompassControl({
      mapboxglService: this,
    });
    this.map.addControl(this.customCompassControl, position);
  };

  /**
   * Thêm mới control graph
   * @param position Vị trí hiển thị
   */
  addGraphControl = async (position: string = "top-right") => {
    this.graphControl = new MapboxglGraphControl({ mapboxglService: this });
    this.map.addControl(this.graphControl, position);
  };

  /**
   * Thêm mới control danh sách lớp bản đồ
   * @param position Vị trí hiển thị
   */
  addLayerControl = async (position: string = "top-right") => {
    this.layerControl = new MapboxglLayerControl({ mapboxglService: this });
    this.map.addControl(this.layerControl, position);
  };

  /**
   * Thêm mới control chuyển bản đồ nền
   * @param position Vị trí hiển thị
   */
  addChangeStyleControl = (position: string = "top-right") => {
    this.changeStyleControl = new MapboxglChangeStyleControl({
      mapboxglService: this,
    });
    this.map.addControl(this.changeStyleControl, position);
  };

  /**
   * Thêm mới control tính diện tích và khoảng cách
   * @param position Vị trí hiển thị
   */
  addCustomDrawControl = (position: string = "top-right") => {
    this.customDrawControl = new MapboxglDrawFeatureControl({
      mapboxglService: this,
    });
    this.map.addControl(this.customDrawControl, position);
  };

  /**
   * Thêm mới control search
   * @param position Vị trí hiển thị
   */
  addSearchControl = (position: string = "top-right") => {
    this.searchControl = new MapboxglSearchControl({ mapboxglService: this });
    this.map.addControl(this.searchControl, position);
  };

  /**
   * Thêm mới control get feature info
   * @param position Vị trí hiển thị
   */
  addFeatureInfoControl = (position: string = "top-right") => {
    this.featureInfoControl = new MapboxglFeatureInfoControl({
      mapboxglService: this,
    });
    this.map.addControl(this.featureInfoControl, position);
  };

  /**
   * Hàm hiển thị/ẩn lớp bản đồ
   * @param map Bản đồ
   * @param layerId Id layers
   * @param layerGroup Layer group
   */
  toggleShowLayer = (layerId: string) => {
    allLayers.forEach((layer: any) => {
      if (layer.id === layerId) {
        layer.display = !layer.display;
        if (this.map.getLayer(`${layer.id}-text`) && layer.textStyle) {
          this.map.setPaintProperty(
            `${layer.id}-text`,
            "text-opacity",
            layer.display ? 1 : 0
          );
        }
        if (this.map.getLayer(`${layer.id}-fill`)) {
          if (layer.polygonStyle) {
            this.map.setPaintProperty(
              `${layer.id}-fill`,
              "fill-opacity",
              layer.display ? layer.polygonStyle.paint["fill-opacity"] : 0
            );
          }
        }
        if (this.map.getLayer(`${layer.id}-line`) && layer.polylineStyle) {
          this.map.setPaintProperty(
            `${layer.id}-line`,
            "line-opacity",
            layer.display ? 1 : 0
          );
        }
        if (this.map.getLayer(`${layer.id}-symbol`) && layer.symbolStyle) {
          this.map.setPaintProperty(
            `${layer.id}-symbol`,
            "icon-opacity",
            layer.display ? 1 : 0
          );
        }
      }
    });
  };

  /**
   * Add layers to map
   * @param layers Layers
   */
  addLayers = (layers: any) => {
    this.layers = [...layers];
    this.layers.forEach(async (layer: any) => {
      // Add source
      if (this.map.getSource(layer.id)) {
        return;
      }
      await this.map.addSource(layer.id, {
        type: "geojson",
        data: layer.url,
      });

      // Add default fill
      await this.map.addLayer({
        id: `${layer.id}-default-fill`,
        type: "fill",
        source: layer.id,
        minzoom: 1,
        maxzoom: 24,
        layout: {},
        paint: {
          "fill-color": "transparent",
        },
      });

      // Add polygon layer
      if (layer.polygonStyle) {
        const style = layer.polygonStyle;
        style.paint["fill-opacity"] = layer.display
          ? layer.polygonStyle.paint["fill-opacity"]
          : 0;
        await this.map.addLayer({
          id: `${layer.id}-fill`,
          type: "fill",
          source: layer.id,
          minzoom: 1,
          maxzoom: 24,
          layout: style.layout,
          paint: style.paint,
          "default-layer": `${layer.id}-default-fill`,
        });
      }

      // Add polyline layer
      if (layer.polylineStyle) {
        const style = layer.polylineStyle;
        style.paint["line-opacity"] = layer.display ? 1 : 0;
        await this.map.addLayer({
          id: `${layer.id}-line`,
          type: "line",
          source: layer.id,
          minzoom: 1,
          maxzoom: 24,
          layout: style.layout,
          paint: style.paint,
          "default-layer": `${layer.id}-default-fill`,
        });
      }

      // Add text style
      if (layer.textStyle) {
        const style = layer.textStyle;
        style.paint["text-opacity"] = layer.display ? 1 : 0;
        await this.map.addLayer({
          id: `${layer.id}-text`,
          type: "symbol",
          source: layer.id,
          minzoom: 1,
          maxzoom: 24,
          layout: style.layout,
          paint: style.paint,
          "default-layer": `${layer.id}-default-fill`,
        });
      }

      // Add point layer
      if (layer.symbolStyle) {
        const style = layer.symbolStyle;
        style.paint["icon-opacity"] = layer.display ? 1 : 0;
        await this.map.addLayer({
          id: `${layer.id}-symbol`,
          type: "symbol",
          source: layer.id,
          minzoom: 1,
          maxzoom: 24,
          paint: style.paint,
          layout: style.layout,
          "default-layer": `${layer.id}-default-fill`,
        });
      }
      this.rearrangeLayersOrder(layer);
      // Check layers order
      // console.log(layers);
      // console.log(this.map.getStyle().layers);
    });
    this.layersOnLoadedSubject.next(null);
  };

  // Rearrange the layers in the right order after await functions
  rearrangeLayersOrder = (layer: { id: any }) => {
    if (
      `${layer.id}-fill` !== "River-fill" &&
      `${layer.id}-fill` !== "Drainage-fill" &&
      `${layer.id}-fill` !== "District_headquarter-fill" &&
      `${layer.id}-line` !== "floodprone-line" &&
      `${layer.id}-line` !== "Wetlands-line"
    ) {
      this.map.moveLayer(`${layer.id}-fill`, `${layer.id}-line`);
      this.map.moveLayer(`${layer.id}-default-fill`, `${layer.id}-fill`);
    }
    // Manual rearrange specific layers that not following the order
    this.map.moveLayer("River-line", "subbasin-line");
    this.map.moveLayer("Municipal_boundary-line", "subbasin-line");
    this.map.moveLayer("Master_plan_boundary-line", "subbasin-line");
    this.map.moveLayer("subbasin-line", "River-line");
    this.map.moveLayer("Drainage-line", "River-line");
    this.map.moveLayer("River-line", "Drainage-line");
    this.map.moveLayer("Dibrugarh_boundary-line", "subbasin-line");

    this.map.moveLayer("District_headquarter-text", "subbasin-text");
    this.map.moveLayer("subbasin-text", "District_headquarter-text");
    this.map.moveLayer("Drainage-text", "District_headquarter-text");
    this.map.moveLayer("River-text", "Drainage-text");
    this.map.moveLayer("Wetlands-text", "River-text");
    this.map.moveLayer("floodprone-text", "Wetlands-text");
    this.map.moveLayer(
      "District_headquarter-symbol",
      "District_headquarter-text"
    );
    this.map.moveLayer(
      "District_headquarter-text",
      "District_headquarter-symbol"
    );
  };

  /**
   * Lấy danh sách feature
   * @param e Map event click e point
   */
  getMapFeature = async (e: any) => {
    const bbox = [
      [e.point.x - 10, e.point.y - 10],
      [e.point.x + 10, e.point.y + 10],
    ];
    const layerIds: any = [];
    allLayers.forEach((layer: any) => {
      if (!layer.display) {
        return;
      }
      layerIds.push(`${layer.id}-default-fill`);
    });
    return this.map.queryRenderedFeatures(bbox, { layers: layerIds });
  };

  /**
   * Di chuyển đến vị trí mong muốn dựa vào bound sau đó highlight đối tượng
   * @param bound Array [[bbox1, bbox2], [bbox3, bbox4]]
   * @param gId null?
   * @param padding number
   */
  mapFitBounds = async (bound: any, gId = null, padding = 10) => {
    if (!this.map) {
      return;
    }

    // Fit bounds
    if (
      bound &&
      bound[0][0] !== 0 &&
      bound[0][1] !== 0 &&
      bound[1][0] !== 0 &&
      bound[1][1] !== 0
    ) {
      await this.map.fitBounds(bound, { duration: 0, padding });
    }
  };
}
